import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createQbQuote } from "../../api/quotes";
import { listTemplates } from "../../api/templates";

export default function NewQuoteDialog({ open, onClose, locations, seasons, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    clientName: "",
    templateId: "",
    seasonId: "",
    locationId: "",
    nights: 7,
    studentPax: 25,
    visitingAdultPax: 2,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    listTemplates({ activeOnly: true }).then((d) => setTemplates(d || [])).catch(() => {});
  }, [open]);

  // When picking a template, prefill the rest of the form (template defaults)
  const onTemplateChange = (templateId) => {
    setForm({ ...form, templateId });
    if (!templateId) return;
    const t = templates.find((x) => x.id === Number(templateId));
    if (!t) return;
    setForm((prev) => ({
      ...prev,
      templateId,
      locationId: prev.locationId || t.defaultLocationId || "",
      seasonId: prev.seasonId || t.defaultSeasonId || "",
      nights: t.defaultNights ?? prev.nights,
      studentPax: t.defaultStudentPax ?? prev.studentPax,
      visitingAdultPax: t.defaultVisitingAdultPax ?? prev.visitingAdultPax,
    }));
  };

  const submit = async () => {
    if (!form.clientName.trim()) { setError("Client name is required"); return; }
    if (!form.seasonId) { setError("Season is required"); return; }
    if (!form.locationId) { setError("Location is required"); return; }
    const body = {
      clientName: form.clientName.trim(),
      seasonId: Number(form.seasonId),
      locationId: Number(form.locationId),
      nights: Number(form.nights) || 1,
      studentPax: Number(form.studentPax) || 0,
      visitingAdultPax: Number(form.visitingAdultPax) || 0,
      ...(form.templateId ? { templateId: Number(form.templateId) } : {}),
    };
    try {
      setSaving(true);
      setError("");
      const created = await createQbQuote(body);
      onCreated?.(created);
    } catch (e) {
      setError(e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Dynamic Quote</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Typography color="error">{error}</Typography>}
          <TextField
            label="Client Name" required fullWidth
            value={form.clientName}
            onChange={(e) => setForm({ ...form, clientName: e.target.value })}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Template (optional)</InputLabel>
            <Select
              label="Template (optional)"
              value={form.templateId}
              onChange={(e) => onTemplateChange(e.target.value)}
            >
              <MenuItem value="">(none)</MenuItem>
              {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Season</InputLabel>
              <Select label="Season" value={form.seasonId} onChange={(e) => setForm({ ...form, seasonId: e.target.value })}>
                <MenuItem value="">(select)</MenuItem>
                {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Location</InputLabel>
              <Select label="Location" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })}>
                <MenuItem value="">(select)</MenuItem>
                {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Nights" type="number" inputProps={{ min: 1 }} fullWidth
              value={form.nights}
              onChange={(e) => setForm({ ...form, nights: e.target.value })}
            />
            <TextField
              label="Student Pax" type="number" inputProps={{ min: 0 }} fullWidth
              value={form.studentPax}
              onChange={(e) => setForm({ ...form, studentPax: e.target.value })}
            />
            <TextField
              label="Visiting Adult Pax" type="number" inputProps={{ min: 0 }} fullWidth
              value={form.visitingAdultPax}
              onChange={(e) => setForm({ ...form, visitingAdultPax: e.target.value })}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
